import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ModificationPanel = ({
  booking,
  modifications,
  onDateChange,
  onAccommodationChange,
  onActivityChange
}) => {
  const [activeSection, setActiveSection] = useState('dates');
  const [dateForm, setDateForm] = useState({
    checkIn: modifications?.dates?.checkIn || booking?.checkIn,
    checkOut: modifications?.dates?.checkOut || booking?.checkOut
  });
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [activityToAdd, setActivityToAdd] = useState('');

  // Mock accommodation options
  const accommodationOptions = [
    { value: 'standard', label: 'Standard Room - ₱3,500/night', price: 3500, available: true },
    { value: 'deluxe', label: 'Deluxe Room - ₱5,000/night', price: 5000, available: true },
    { value: 'suite', label: 'Suite - ₱8,000/night', price: 8000, available: false },
    { value: 'villa', label: 'Private Villa - ₱12,000/night', price: 12000, available: true }
  ];

  // Mock activity options
  const activityOptions = [
    { value: 'island-hopping', label: 'Island Hopping Tour - ₱1,500', price: 1500 },
    { value: 'snorkeling', label: 'Snorkeling Adventure - ₱800', price: 800 },
    { value: 'sunset-cruise', label: 'Sunset Cruise - ₱2,000', price: 2000 },
    { value: 'spa', label: 'Spa Treatment - ₱1,200', price: 1200 },
    { value: 'diving', label: 'Scuba Diving - ₱3,500', price: 3500 }
  ];

  const handleDateSubmit = () => {
    if (dateForm?.checkIn && dateForm?.checkOut) {
      onDateChange(dateForm);
    }
  };

  const handleAccommodationSelect = (value) => {
    const selected = accommodationOptions?.find(opt => opt?.value === value);
    setSelectedAccommodation(selected);
  };

  const handleAccommodationSubmit = () => {
    if (selectedAccommodation) {
      onAccommodationChange(selectedAccommodation);
    }
  };

  const handleAddActivity = () => {
    if (activityToAdd) {
      const activity = activityOptions?.find(opt => opt?.value === activityToAdd);
      if (activity) {
        onActivityChange({
          ...modifications?.activities,
          added: [...(modifications?.activities?.added || []), activity]
        });
        setActivityToAdd('');
      }
    }
  };

  const handleRemoveAddedActivity = (index) => {
    const newAdded = [...(modifications?.activities?.added || [])];
    newAdded?.splice(index, 1);
    onActivityChange({
      ...modifications?.activities,
      added: newAdded
    });
  };

  const sections = [
    { id: 'dates', label: 'Dates', icon: 'Calendar' },
    { id: 'accommodation', label: 'Accommodation', icon: 'Building' },
    { id: 'activities', label: 'Activities', icon: 'Compass' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-secondary to-accent p-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Icon name="Edit" size={20} />
          Modify Booking
        </h2>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-border">
        {sections?.map((section) => (
          <button
            key={section?.id}
            onClick={() => setActiveSection(section?.id)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === section?.id
                ? 'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={section?.icon} size={16} />
            <span className="hidden sm:inline">{section?.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Dates Section */}
        {activeSection === 'dates' && (
          <div className="space-y-4">
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Select new check-in and check-out dates. A modification fee of ₱500 applies.
                </p>
              </div>
            </div>

            <Input
              type="date"
              label="New Check-in Date"
              value={dateForm?.checkIn}
              onChange={(e) => setDateForm(prev => ({ ...prev, checkIn: e?.target?.value }))}
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />

            <Input
              type="date"
              label="New Check-out Date"
              value={dateForm?.checkOut}
              onChange={(e) => setDateForm(prev => ({ ...prev, checkOut: e?.target?.value }))}
              min={dateForm?.checkIn}
            />

            {modifications?.dates && (
              <div className="p-3 bg-success/10 rounded-lg border border-success/20">
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-medium">Dates updated</span>
                </div>
              </div>
            )}

            <Button
              onClick={handleDateSubmit}
              className="w-full"
              disabled={!dateForm?.checkIn || !dateForm?.checkOut}
            >
              <Icon name="Calendar" size={18} />
              Update Dates
            </Button>
          </div>
        )}

        {/* Accommodation Section */}
        {activeSection === 'accommodation' && (
          <div className="space-y-4">
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Upgrade or change your accommodation. A modification fee of ₱300 applies.
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Select New Accommodation
              </label>
              <Select
                options={accommodationOptions?.map(opt => ({
                  value: opt?.value,
                  label: opt?.available ? opt?.label : `${opt?.label} (Unavailable)`
                }))}
                value={selectedAccommodation?.value || ''}
                onChange={handleAccommodationSelect}
                placeholder="Choose accommodation type"
              />
            </div>

            {selectedAccommodation && (
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Selected</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    selectedAccommodation?.available
                      ? 'bg-success/10 text-success' :'bg-error/10 text-error'
                  }`}>
                    {selectedAccommodation?.available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
                <p className="text-sm text-foreground font-semibold">
                  {selectedAccommodation?.label}
                </p>
              </div>
            )}

            {modifications?.accommodation && (
              <div className="p-3 bg-success/10 rounded-lg border border-success/20">
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-medium">Accommodation updated</span>
                </div>
              </div>
            )}

            <Button
              onClick={handleAccommodationSubmit}
              className="w-full"
              disabled={!selectedAccommodation || !selectedAccommodation?.available}
            >
              <Icon name="Building" size={18} />
              Update Accommodation
            </Button>
          </div>
        )}

        {/* Activities Section */}
        {activeSection === 'activities' && (
          <div className="space-y-4">
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Add new activities to your booking. Activity costs will be added to your total.
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Add Activity
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                    options={activityOptions}
                    value={activityToAdd}
                    onChange={setActivityToAdd}
                    placeholder="Choose activity"
                  />
                </div>
                <Button
                  onClick={handleAddActivity}
                  disabled={!activityToAdd}
                  className="flex-shrink-0"
                >
                  <Icon name="Plus" size={18} />
                </Button>
              </div>
            </div>

            {/* Added Activities */}
            {modifications?.activities?.added?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">Added Activities</h4>
                {modifications?.activities?.added?.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-success/10 rounded-lg border border-success/20"
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="Plus" size={16} className="text-success" />
                      <span className="text-sm text-foreground">{activity?.label}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveAddedActivity(index)}
                      className="text-error hover:text-error/80 transition-colors"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {modifications?.activities?.added?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="Compass" size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No activities added yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModificationPanel;